import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { Icon } from "@iconify/react/dist/iconify.js";
import QRCode from 'qrcode';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { createHierarchyStructure, getHierarchyStructureById, getHierarchyStructures } from '../api/hierarchy-structureAPI';
import { getColours } from '../api/colours.API';
import { validateForm } from '../utils/useValidator';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  getPaginatedGlobalGroupsCombined,
  getGlobalGroupById,
} from '../api/globalGroupAPI';
import { createGroup, getGroupById as getLocalGroupById, checkGroupNameExists } from '../api/groupAPI';
import { createAsset, getAssetCountSummary, getAssets } from '../api/assetAPI';
import { createArea } from '../api/areaAPI';
import { getAreas } from '../api/areaAPI';
import { clearCache } from '../utils/lookupCache';
import { createWorkOrder, getWorkOrderCountSummary, updateWorkOrder } from '../api/work-orderAPI';
import { createEvents } from '../api/eventsAPI';
import { createTeams, getTeams, getTeamsById } from '../api/teamsAPI';
import { createInvites, checkInviteEmailAccount } from '../api/invitesAPI';
import { getPlanningType } from '../api/planning-typeAPI';
import { getAssetType } from '../api/assetTypeAPI';
import {
  createCostCentre,
  checkCostCentreDuplicate,
  getCostCentre,
  getCostCentreById,
  clearCostCentreCache,
} from '../api/cost-centreAPI';
import { useFormBuilder } from '../utils/useFormBuilder';
import { useSetting } from '../utils/AppSettingsContext';
import { getSetupTasks, completeSetupTask } from '../api/setupTasksAPI';
import { showToastError, showToastWarning } from '../utils/errorController';
import { getSetupStateKey, readSetupState, writeSetupState } from '../utils/setupState';


const PRODUCT_TOUR_TRIGGER_KEY = 'samurai:product-tour:trigger';
const PRODUCT_TOUR_TRIGGER_ONCE_KEY = 'samurai:product-tour:trigger-once';

const steps = [
  { key: 'site', title: 'Add a Site or Department', icon: 'mage:building-a', description: '' },
  { key: 'team', title: 'Add a Work Team', icon: 'mage:users', description: '' },
  { key: 'invite', title: 'Invite Team Members', icon: 'mage:user', description: '' },
  { key: 'asset', title: 'Add an Asset', icon: 'mage:delivery-truck', description: '' },
  { key: 'workorder', title: 'Add Your First Work Order', icon: 'mage:checklist-note', description: '' },
];
const HOME_SKIP_AUTO_TOUR_ONCE_KEY = 'samurai:home:skip-auto-tour-once';

const SetupLayer = () => {
  const navigate = useNavigate();
  const userProfile = useUserProfile();
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [completed, setCompleted] = useState({});
  const [addressOpen, setAddressOpen] = useState(false);
  const [appPhoneNumber, setAppPhoneNumber] = useState('');
  const [appLinkMessage, setAppLinkMessage] = useState('');
  const [androidQrCodeUrl, setAndroidQrCodeUrl] = useState('');
  const [iosQrCodeUrl, setIosQrCodeUrl] = useState('');
  const setupStateKey = useMemo(
    () => (userProfile?.id ? getSetupStateKey(userProfile.id) : null),
    [userProfile?.id]
  );

  // Step data
  const [site, setSite] = useState({
    name: '',
    addressL1: '',
    addressL2: '',
    city: '',
    state: '',
    postCode: '',
    phone: '',
  });

  useEffect(() => {
    if (userProfile?.organisation) {
      const org = userProfile.organisation;
      setSite(prev => ({
        ...prev,
        addressL1: org.orgAddress || prev.addressL1,
        city: org.orgCity || prev.city,
        state: org.orgState || prev.state,
        postCode: org.orgZip || prev.postCode,
        phone: org.orgPhone || prev.phone,
      }));
    }
  }, [userProfile]);

  useEffect(() => {
    const loadCompleted = async () => {
      try {
        const tasks = await getSetupTasks();
        const mapped = {};
        tasks.forEach((t) => {
          const index = steps.findIndex((s) => s.key === t.taskKey);
          if (index >= 0) mapped[index + 1] = true;
        });
        setCompleted(mapped);
        const firstIncomplete = steps.findIndex((_, i) => !mapped[i + 1]);
        setCurrentStep(firstIncomplete >= 0 ? firstIncomplete + 1 : steps.length + 1);
      } catch (err) {
        console.error('Error loading setup tasks', err);
      }
    };
    loadCompleted();
  }, []);

  useEffect(() => {
    const loadPrerequisites = async () => {
      try {
        const [hierarchyRecords, teamRecords, assetCountSummary, areaRecords, workOrderCountSummary] = await Promise.all([
          getHierarchyStructures(),
          getTeams(),
          getAssetCountSummary(),
          getAreas({ page: 1, pageSize: 1, sortField: 'id', sortOrder: 'DESC' }),
          getWorkOrderCountSummary(),
        ]);
        setHasHierarchyStructureRecords(Array.isArray(hierarchyRecords) && hierarchyRecords.length > 0);
        setHasTeamsRecords(getListItems(teamRecords).length > 0);
        const assetCount = Number(assetCountSummary?.count) || 0;
        const hasMoreThanOneAsset = Boolean(assetCountSummary?.hasMoreThanOne);
        setHasAssetRecords(assetCount > 0);
        setHasMultipleAssetRecords(hasMoreThanOneAsset);
        setHasAreaRecords(getListItems(areaRecords).length > 0);
        const hasMoreThanOne = Boolean(workOrderCountSummary?.hasMoreThanOne);
        setHasMultipleWorkOrderRecords(hasMoreThanOne);

        if (hasMoreThanOneAsset) {
          setCompleted((prev) => ({ ...prev, 4: true }));
          try {
            await completeSetupTask('asset');
          } catch (err) {
            console.error('Error recording asset setup task completion', err);
          }
        }

        if (hasMoreThanOne) {
          setCompleted((prev) => ({ ...prev, 5: true }));
          try {
            await completeSetupTask('workorder');
          } catch (err) {
            console.error('Error recording workorder setup task completion', err);
          }
        }
      } catch (err) {
        console.error('Error loading prerequisite records', err);
        setHasHierarchyStructureRecords(false);
        setHasTeamsRecords(false);
        setHasAssetRecords(false);
        setHasMultipleAssetRecords(false);
        setHasAreaRecords(false);
        setHasMultipleWorkOrderRecords(false);
      } finally {
        setPrereqsLoaded(true);
      }
    };
    loadPrerequisites();
  }, []);

  const [team, setTeam] = useState({ teamName: '', colourId: '' });
  const [teamId, setTeamId] = useState(null);
  const [colours, setColours] = useState([]);
  const [invites, setInvites] = useState([{ email: '' }]);
  const [assetType] = useState('asset');
  const [asset, setAsset] = useState({
    AssetTypeId: '',
    groupId: '',
    PlanningTypeId: '',
    assetNumber: '',
    assetSerial: '',
    costCenterId: '',
    assetStartDate: '',
    assetEndDate: '',
    assetDepartmentId: '',
    assetImage: '',
    assetDescription: '',
    plantStatusId: 6,
  });
  const [area, setArea] = useState({ code: '', name: '', costCenterId: '', image: '' });
  const [workOrder, setWorkOrder] = useState({
    taskTypeId: 4,
    workOrderTitle: '',
    workOrderDescription: '',
    scheduledAt: '',
    dueDate: '',
    assetId: null,
    areaId: null,
    workOrderImage: '',
  });
  const [planningTypes, setPlanningTypes] = useState([]);
  const [assetTypes, setAssetTypes] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedAssetCostCentre, setSelectedAssetCostCentre] = useState(null);
  const [selectedAreaCostCentre, setSelectedAreaCostCentre] = useState(null);
  const [costCentreTarget, setCostCentreTarget] = useState(null);
  const [showAddCostCentreModal, setShowAddCostCentreModal] = useState(false);
  const [newCostCentre, setNewCostCentre] = useState({});
  const [costCentreErrors, setCostCentreErrors] = useState({});
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [newGroup, setNewGroup] = useState({});
  const [groupErrors, setGroupErrors] = useState({});
  const [createdAssetId, setCreatedAssetId] = useState(null);
  const [createdAreaId] = useState(null);
  const [lastCreatedTarget, setLastCreatedTarget] = useState({ type: null, id: null });
  const [departmentId, setDepartmentId] = useState(null);
  const [hasHierarchyStructureRecords, setHasHierarchyStructureRecords] = useState(false);
  const [hasTeamsRecords, setHasTeamsRecords] = useState(false);
  const [hasAssetRecords, setHasAssetRecords] = useState(false);
  const [hasAreaRecords, setHasAreaRecords] = useState(false);
  const [hasMultipleAssetRecords, setHasMultipleAssetRecords] = useState(false);
  const [hasMultipleWorkOrderRecords, setHasMultipleWorkOrderRecords] = useState(false);
  const [prereqsLoaded, setPrereqsLoaded] = useState(false);
  const prereqWarningShownRef = useRef(false);
  const costCentreOptionCacheRef = useRef(new Map());
  const organisationId =
    userProfile?.org_id ?? userProfile?.organisation?.id ?? userProfile?.organisationId ?? 1;
  const defaultCostCentre = useSetting('default_cost_centre');
  const defaultCostCentreId =
    defaultCostCentre !== null && defaultCostCentre !== undefined
      ? Number(defaultCostCentre) || null
      : null;
  const androidCompanionAppLink = 'https://play.google.com/store/apps/details?id=com.samurai.cmms&hl=en;';
  const iosCompanionAppLink = 'https://apps.apple.com/app/samurai-cmms/id6753664059 ';
  const googlePlayBadgeImage = '/assets/images/google-play.png';
  const appStoreBadgeImage = 'https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83';

  useEffect(() => {
    const qrOptions = {
      width: 100,
      color: {
        dark: '#0d6efd',
        light: '#ffffff',
      },
    };

    QRCode.toDataURL(androidCompanionAppLink, qrOptions)
      .then(setAndroidQrCodeUrl)
      .catch((error) => console.error('Failed to generate Android QR code', error));

    QRCode.toDataURL(iosCompanionAppLink, qrOptions)
      .then(setIosQrCodeUrl)
      .catch((error) => console.error('Failed to generate iOS QR code', error));
  }, [androidCompanionAppLink, iosCompanionAppLink]);

  useEffect(() => {
    if (!setupStateKey) {
      return;
    }

    const storedState = readSetupState(setupStateKey);
    if (!storedState) {
      return;
    }

    const storedSiteName = storedState.siteName || storedState.site?.name || '';

    if (storedSiteName) {
      setSite((prev) => ({
        ...prev,
        name: storedSiteName,
      }));
    }

    if (storedState.departmentId) {
      setDepartmentId(storedState.departmentId);

      (async () => {
        try {
          const department = await getHierarchyStructureById(storedState.departmentId);
          setSite((prev) => ({
            ...prev,
            name: department?.name || storedSiteName || prev.name,
          }));
        } catch (err) {
          console.error('Error restoring saved site or department', err);
          setDepartmentId(null);
          writeSetupState(setupStateKey, {
            ...storedState,
            departmentId: null,
            siteName: storedSiteName,
            site: {
              ...(storedState.site || {}),
              name: storedSiteName,
            },
          });
        }
      })();
    }

    const storedTeamName = storedState.teamName || storedState.team?.teamName || '';
    const storedTeamColourId = storedState.teamColourId ?? storedState.team?.colourId ?? '';
    const storedTeamId = storedState.teamId ?? storedState.team?.id ?? null;

    if (storedTeamName || storedTeamColourId || storedTeamId) {
      setTeam((prev) => ({
        ...prev,
        teamName: storedTeamName || prev.teamName,
        colourId: storedTeamColourId || prev.colourId,
      }));
      setTeamId(storedTeamId);

      if (storedTeamId) {
        (async () => {
          try {
            await getTeamsById(storedTeamId);
          } catch (err) {
            console.warn('Stored team id could not be restored, clearing stale team state', err);
            setTeamId(null);
            writeSetupState(setupStateKey, {
              ...storedState,
              teamId: null,
              teamName: storedTeamName,
              teamColourId: storedTeamColourId,
              team: {
                ...(storedState.team || {}),
                id: null,
                teamName: storedTeamName,
                colourId: storedTeamColourId,
              },
            });
          }
        })();
      }
    }

    const storedInvites = Array.isArray(storedState.invites) ? storedState.invites : [];
    if (storedInvites.length > 0) {
      setInvites(
        storedInvites.map((invite) => ({
          email: typeof invite === 'string' ? invite : invite?.email || '',
        })),
      );
    }
  }, [setupStateKey]);

  useEffect(() => {
    if (!setupStateKey) {
      return;
    }

    const hasMeaningfulData = Boolean(
      departmentId ||
      site.name?.trim() ||
      teamId ||
      team.teamName?.trim() ||
      team.colourId ||
      invites.some((invite) => invite.email?.trim())
    );

    if (!hasMeaningfulData) {
      return;
    }

    writeSetupState(setupStateKey, {
      departmentId,
      siteName: site.name?.trim() || '',
      site: {
        name: site.name?.trim() || '',
      },
      teamId,
      teamName: team.teamName?.trim() || '',
      teamColourId: team.colourId ?? '',
      team: {
        id: teamId,
        teamName: team.teamName?.trim() || '',
        colourId: team.colourId ?? '',
      },
      invites: invites.map((invite) => ({
        email: invite.email?.trim() || '',
      })),
    });
  }, [setupStateKey, departmentId, site, team, teamId, invites]);

  const isAdvancedType = useMemo(() => {
    const desc = planningTypes.find((p) => p.id === asset.PlanningTypeId)?.typeDescription;
    return desc?.toLowerCase() === 'advanced';
  }, [planningTypes, asset.PlanningTypeId]);

  const step1Fields = useMemo(
    () => [
      { field: 'name', label: 'Name', type: 'text', size: 12, required: true, placeholder: 'Example: Head Office' },
      { field: 'addressL1', label: 'Address', type: 'text', size: 12 },
      { field: 'addressL2', label: 'Address 2', type: 'text', size: 12 },
      { field: 'city', label: 'City', type: 'text', size: 6 },
      { field: 'state', label: 'State', type: 'text', size: 3 },
      { field: 'postCode', label: 'Post Code', type: 'text', size: 3 },
      { field: 'phone', label: 'Phone', type: 'text', size: 12 },
    ],
    []
  );

  const step2Fields = useMemo(
    () => [
      { field: 'teamName', label: 'Team Name', type: 'text', size: 12, required: true, placeholder: 'Example: Maintenance Crew' },
      {
        field: 'colourId',
        label: 'Team Colour',
        type: 'select',
        size: 12,
        options: colours.map(c => ({ value: c.id, label: c.name, colourCode: c.bootstrapName })),
        formatOptionLabel: o => (
          <div className="d-flex align-items-center">
            <span
              className={`bg-${o.colourCode}`}
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                display: 'inline-block',
                marginRight: 5,
              }}
            />
            {o.label}
          </div>
        ),
      },
    ],
    [colours]
  );

  const handleGroupSearch = async (inputValue, meta = {}) => {
    try {
      const trimmed = typeof inputValue === 'string' ? inputValue.trim() : '';
      const page = Number(meta?.page) || 1;
      const shouldLoadDefault = !trimmed;
      const createOption = {
        value: '__create_new__',
        isCreate: true,
        inputValue: trimmed,
        label: 'Add New Model/Group',
        tooltip: 'Add New Model or Group',
      };

      if (!shouldLoadDefault && trimmed.length < 2) {
        return {
          data: page === 1 ? [createOption] : [],
          total: page === 1 ? 1 : 0,
          pageSize: 10,
        };
      }

      const resp = await getPaginatedGlobalGroupsCombined({
        page,
        pageSize: 10,
        search: shouldLoadDefault ? '' : trimmed,
      });
      const items = resp.data || resp;
      const mapped = items.map((g) => ({
        value: g.id,
        isLocal: Boolean(g.isLocal),
        label: g.groupName || '',
        groupImage: g.groupImage || '',
        assetDescription: `${g.globalManufacturer?.manufacturer || g.manufacturer?.manufacturer || ''} ${g.groupName || ''} ${g.globalClass?.className || g.class?.className || ''}`.trim(),
        tooltip: `${g.globalManufacturer?.manufacturer || ''} ${g.groupName || ''} ${g.globalClass?.className || ''}`.trim(),
      }));
      if (page === 1) {
        mapped.unshift(createOption);
      }
      return {
        data: mapped,
        total: typeof resp.total === 'number' ? resp.total : mapped.length,
        pageSize: 10,
      };
    } catch (err) {
      console.error('Error fetching groups:', err);
      return [];
    }
  };

  const formatGroupOptionLabel = (option) => (
    option?.isCreate ? (
      <div
        className="d-flex align-items-center gap-2 rounded-2 px-12 py-8 border border-primary bg-primary-50 text-primary fw-semibold"
        title={option?.tooltip || option?.label || ''}
        style={{ lineHeight: 1.1 }}
      >
        <Icon icon="mage:plus" className="text-lg" />
        <span>{option?.label || 'Add item'}</span>
      </div>
    ) : (
      <span title={option?.tooltip || option?.label || ''}>
        {option?.label || ''}
      </span>
    )
  );

  const formatCostCentreOption = useCallback((centre) => {
    if (!centre || typeof centre !== 'object') {
      return null;
    }

    const code = String(centre.costCentreCode || '').trim();
    const description = String(centre.costCenterDescription || '').trim();

    return {
      value: centre.id,
      label: code || description || '',
      tooltip: description || code || '',
    };
  }, []);

  const rememberCostCentreOption = useCallback((centre) => {
    const option = formatCostCentreOption(centre);
    if (option?.value !== undefined && option?.value !== null) {
      costCentreOptionCacheRef.current.set(Number(option.value), option);
    }
    return option;
  }, [formatCostCentreOption]);

  const hydrateCostCentreSelection = useCallback(async (id, setSelectedOption) => {
    const numericId = Number(id);
    if (!numericId) {
      setSelectedOption(null);
      return;
    }

    const cached = costCentreOptionCacheRef.current.get(numericId);
    if (cached) {
      setSelectedOption(cached);
      return;
    }

    try {
      const record = await getCostCentreById(numericId);
      const option = rememberCostCentreOption(record);
      setSelectedOption(option);
    } catch (err) {
      console.error('Error restoring cost centre selection', err);
      setSelectedOption(null);
    }
  }, [rememberCostCentreOption]);

  const handleCostCentreSearch = useCallback(async (inputValue, meta = {}) => {
    try {
      const trimmed = typeof inputValue === 'string' ? inputValue.trim() : '';
      const page = Number(meta?.page) || 1;
      if (trimmed && trimmed.length < 1) {
        return [];
      }

      const resp = await getCostCentre({
        search: trimmed,
      });

      const items = Array.isArray(resp)
        ? resp
        : Array.isArray(resp?.data)
          ? resp.data
          : Array.isArray(resp?.results)
            ? resp.results
            : [];
      const mapped = items
        .map((centre) => rememberCostCentreOption(centre))
        .filter(Boolean);

      if (page === 1) {
        mapped.unshift({
          value: '__create_cost_centre__',
          isCreate: true,
          inputValue: trimmed,
          label: 'Add New Cost Centre',
          tooltip: 'Add New Cost Centre',
        });
      }

      return {
        data: mapped,
        total: typeof resp?.total === 'number' ? resp.total : mapped.length,
        pageSize: mapped.length || 1,
        hasMore: false,
      };
    } catch (err) {
      console.error('Error fetching cost centres:', err);
      return [];
    }
  }, [rememberCostCentreOption]);

  const formatCostCentreOptionLabel = useCallback((option) => (
    option?.isCreate ? (
      <div
        className="d-flex align-items-center gap-2 rounded-2 px-12 py-8 border border-primary bg-primary-50 text-primary fw-semibold"
        title={option?.tooltip || option?.label || ''}
        style={{ lineHeight: 1.1 }}
      >
        <Icon icon="mage:plus" className="text-lg" />
        <span>{option?.label || 'Add New Cost Centre'}</span>
      </div>
    ) : (
      <span title={option?.tooltip || option?.label || ''}>
        {option?.label || ''}
      </span>
    )
  ), []);

  const costCentreInputFields = useMemo(
    () => [
      { field: 'costCentreCode', label: 'Cost Centre Code', type: 'text', size: 6, required: true },
      { field: 'costCenterDescription', label: 'Description', type: 'text', size: 6, required: true },
    ],
    [],
  );

  const { renderField: renderCostCentreField } = useFormBuilder(
    costCentreInputFields,
    newCostCentre,
    setNewCostCentre,
    costCentreErrors,
  );

  const handleOpenAddCostCentreModal = useCallback((seedValue = '', target = null) => {
    setCostCentreTarget(target);
    setNewCostCentre((prev) => ({
      ...prev,
      costCentreCode: seedValue || prev.costCentreCode || '',
      costCenterDescription: prev.costCenterDescription || '',
    }));
    setCostCentreErrors({});
    setShowAddCostCentreModal(true);
  }, []);

  const handleCloseAddCostCentreModal = useCallback(() => {
    setShowAddCostCentreModal(false);
    setCostCentreTarget(null);
    setNewCostCentre({});
    setCostCentreErrors({});
  }, []);

  const handleCostCentreCreateSave = useCallback(async () => {
    const trimmedCode = String(newCostCentre.costCentreCode || '').trim();
    const trimmedDescription = String(newCostCentre.costCenterDescription || '').trim();
    const newErrors = {};

    if (!trimmedCode) newErrors.costCentreCode = 'Code is required';
    if (!trimmedDescription) newErrors.costCenterDescription = 'Description is required';

    setCostCentreErrors(newErrors);
    if (Object.keys(newErrors).length) {
      return;
    }

    try {
      const dup = await checkCostCentreDuplicate(trimmedCode, trimmedDescription, null);
      const duplicateErrors = {};
      if (dup?.codeExists) {
        duplicateErrors.costCentreCode = 'Code already exists';
      }
      if (dup?.nameExists) {
        duplicateErrors.costCenterDescription = 'Description already exists';
      }
      if (Object.keys(duplicateErrors).length > 0) {
        setCostCentreErrors((prev) => ({ ...prev, ...duplicateErrors }));
        return;
      }

      const created = await createCostCentre({
        ...newCostCentre,
        costCentreCode: trimmedCode,
        costCenterDescription: trimmedDescription,
      });

      clearCostCentreCache();
      const option = formatCostCentreOption(created);
      if (option) {
        costCentreOptionCacheRef.current.set(Number(option.value), option);
        if (costCentreTarget === 'asset') {
          setSelectedAssetCostCentre(option);
          setAsset((prev) => ({ ...prev, costCenterId: option.value }));
        } else if (costCentreTarget === 'area') {
          setSelectedAreaCostCentre(option);
          setArea((prev) => ({ ...prev, costCenterId: option.value }));
        } else {
          setSelectedAssetCostCentre(option);
          setSelectedAreaCostCentre(option);
          setAsset((prev) => ({ ...prev, costCenterId: option.value }));
          setArea((prev) => ({ ...prev, costCenterId: option.value }));
        }
      }
      window.dispatchEvent(new Event('cost-centres-updated'));
      setShowAddCostCentreModal(false);
      setCostCentreTarget(null);
      setNewCostCentre({});
      setCostCentreErrors({});
    } catch (error) {
      console.error('Error creating cost centre:', error);
      const message = error?.response?.data?.message || error?.message || 'Error creating cost centre';
      showToastError(Array.isArray(message) ? message[0] : message);
    }
  }, [
    formatCostCentreOption,
    newCostCentre,
    setArea,
    setAsset,
    setCostCentreErrors,
    costCentreTarget,
    setSelectedAreaCostCentre,
    setSelectedAssetCostCentre,
    setShowAddCostCentreModal,
    setNewCostCentre,
  ]);

  const groupInputFields = useMemo(
    () => [
      { field: 'groupName', label: 'Model or Group Name', type: 'text', size: 6, required: true },
      { field: 'classId', label: 'Class', type: 'select', size: 6, api: 'global-class', displayField: 'className', required: true },
      { field: 'groupDescription', label: 'Description', type: 'textarea', size: 6 },
      {
        field: 'manufacturerId',
        label: 'Manufacturer',
        type: 'select',
        size: 6,
        api: 'manufacturers',
        displayField: 'manufacturer',
        required: true,
      },
      { field: 'groupImage', label: 'Group Image', type: 'image', size: 6, directory: 'modelimages' },
      { field: 'generalSpacer1', label: '', type: 'empty', size: 6, required: false },
      { field: 'generalSpacer2', label: '', type: 'empty', size: 6, required: false },
      { field: 'generalSpacer3', label: '', type: 'empty', size: 6, required: false },
    ],
    []
  );

  const { renderField: renderGroupField } = useFormBuilder(groupInputFields, newGroup, setNewGroup, groupErrors);

  const handleGroupCreateSave = async () => {
    const newErrors = validateForm(groupInputFields, newGroup);
    setGroupErrors(newErrors);
    if (Object.keys(newErrors).length) {
      return;
    }

    try {
      const { exists } = await checkGroupNameExists({ groupName: newGroup.groupName });
      if (exists) {
        setGroupErrors((prev) => ({
          ...prev,
          groupName: 'Group name already exists',
        }));
        return;
      }

      const cleanPayload = {};
      for (const key in newGroup) {
        if (newGroup[key] !== null && typeof newGroup[key] !== 'object') {
          cleanPayload[key] = newGroup[key];
        }
      }

      const created = await createGroup(cleanPayload);
      const grp = await getLocalGroupById(created.id);
      const option = {
        value: grp.id,
        label: grp.groupName || '',
        isLocal: true,
        groupImage: grp.groupImage || '',
        assetDescription: `${grp.manufacturer?.manufacturer || ''} ${grp.groupName || ''} ${grp.class?.className || ''}`.trim(),
      };
      setSelectedGroup(option);
      setAsset((prev) => ({
        ...prev,
        groupId: grp.id,
        assetDescription: `${grp.manufacturer?.manufacturer || ''} ${grp.groupName || ''} ${grp.class?.className || ''}`.trim(),
        assetImage: grp.groupImage || prev.assetImage,
      }));
      clearCache('groups');
      setShowAddGroupModal(false);
      setNewGroup({});
      setGroupErrors({});
    } catch (error) {
      console.error('Error creating group:', error);
      const message = error?.response?.data?.message || error?.message || 'Error creating group';
      showToastError(Array.isArray(message) ? message[0] : message);
    }
  };

  const assetFields = useMemo(
    () => [
      {
        field: 'AssetTypeId',
        label: 'Asset Type',
        type: 'select',
        size: 6,
        api: 'asset-type',
        displayField: 'name',
        required: true,
      },
      {
        field: 'groupId',
        label: 'Asset Group',
        type: 'paginated-select',
        size: 6,
        loadOptions: handleGroupSearch,
        selectedOption: selectedGroup,
        setSelectedOption: setSelectedGroup,
        isClearable: true,
        onChange: (opt, setFormData) => {
          if (!opt) {
            setSelectedGroup(null);
            setFormData((prev) => ({ ...prev, groupId: null, assetImage: '', assetDescription: '' }));
            return;
          }

          if (opt?.isCreate) {
            setShowAddGroupModal(true);
            setNewGroup((prev) => ({
              ...prev,
              groupName: opt.inputValue || prev.groupName || '',
            }));
            setSelectedGroup(null);
            setFormData((prev) => ({
              ...prev,
              groupId: null,
            }));
            return;
          }

          setSelectedGroup(opt);
          setFormData((prev) => ({
            ...prev,
            groupId: opt?.value || null,
            assetImage: opt?.groupImage || prev.assetImage,
            assetDescription: opt?.assetDescription || prev.assetDescription,
          }));

          (async () => {
            try {
              const selectedGroupId = Number(opt?.value);
              if (!selectedGroupId) return;

              if (opt?.isLocal) {
                const localGroup = await getLocalGroupById(selectedGroupId);
                setFormData((prev) => ({
                  ...prev,
                  assetImage: localGroup?.groupImage || '',
                  assetDescription: `${localGroup?.manufacturer?.manufacturer || ''} ${localGroup?.groupName || ''} ${localGroup?.class?.className || ''}`.trim(),
                }));
                return;
              }

              const globalGroup = await getGlobalGroupById(selectedGroupId);
              setFormData((prev) => ({
                ...prev,
                assetImage: globalGroup?.groupImage || '',
                assetDescription: `${globalGroup?.globalManufacturer?.manufacturer || ''} ${globalGroup?.groupName || ''} ${globalGroup?.globalClass?.className || ''}`.trim(),
              }));
            } catch (error) {
              console.error('Error hydrating selected asset group details', error);
            }
          })();
        },
        formatOptionLabel: formatGroupOptionLabel,
        required: true,
      },
      {
        field: 'PlanningTypeId',
        label: 'Planning Type',
        type: 'select',
        size: 6,
        api: 'planning-types',
        displayField: 'typeDescription',
        required: true,
      },
      { field: 'assetNumber', label: 'Asset Number', type: 'text', size: 6, required: true },
      { field: 'assetSerial', label: 'Serial Number', type: 'text', size: 6, required: isAdvancedType },
      {
        field: 'costCenterId',
        label: 'Cost Centre',
        type: 'paginated-select',
        size: 6,
        loadOptions: handleCostCentreSearch,
        minSearchLength: 1,
        selectedOption: selectedAssetCostCentre,
        setSelectedOption: setSelectedAssetCostCentre,
        formatOptionLabel: formatCostCentreOptionLabel,
        onMenuOpen: () => handleCostCentreSearch('', { page: 1 }),
        isClearable: true,
        onChange: (opt, setFormData) => {
          if (!opt) {
            setSelectedAssetCostCentre(null);
            setFormData((prev) => ({ ...prev, costCenterId: null }));
            return;
          }

          if (opt?.isCreate) {
            handleOpenAddCostCentreModal(opt.inputValue || '', 'asset');
            setSelectedAssetCostCentre(null);
            setFormData((prev) => ({ ...prev, costCenterId: null }));
            return;
          }

          setSelectedAssetCostCentre(opt);
          setFormData((prev) => ({ ...prev, costCenterId: opt?.value ?? null }));
        },
        required: true,
      },
      { field: 'assetStartDate', label: 'Start Date', type: 'date', size: 6, required: true },
      {
        field: 'assetEndDate',
        label: 'End Date',
        type: 'date',
        size: 6,
        required: true,
        mustBeAfterField: 'assetStartDate',
        mustBeAfterFieldMessage: 'Due date must be after date start',
      },
      ...(asset.assetImage
        ? [{ field: 'assetImage', label: 'Asset Image', type: 'image', size: 12, directory: 'modelimages' }]
        : []),
    ],
    [asset.assetImage, formatCostCentreOptionLabel, handleCostCentreSearch, isAdvancedType, selectedAssetCostCentre, selectedGroup]
  );

  const areaFields = useMemo(
    () => [
      { field: 'code', label: 'Code', type: 'text', size: 6, required: true, disabled: Boolean(createdAreaId) },
      { field: 'name', label: 'Name', type: 'text', size: 6, required: true, disabled: Boolean(createdAreaId) },
      {
        field: 'costCenterId',
        label: 'Cost Centre',
        type: 'paginated-select',
        size: 6,
        loadOptions: handleCostCentreSearch,
        minSearchLength: 1,
        selectedOption: selectedAreaCostCentre,
        setSelectedOption: setSelectedAreaCostCentre,
        formatOptionLabel: formatCostCentreOptionLabel,
        onMenuOpen: () => handleCostCentreSearch('', { page: 1 }),
        isClearable: true,
        onChange: (opt, setFormData) => {
          if (!opt) {
            setSelectedAreaCostCentre(null);
            setFormData((prev) => ({ ...prev, costCenterId: null }));
            return;
          }

          if (opt?.isCreate) {
            handleOpenAddCostCentreModal(opt.inputValue || '', 'area');
            setSelectedAreaCostCentre(null);
            setFormData((prev) => ({ ...prev, costCenterId: null }));
            return;
          }

          setSelectedAreaCostCentre(opt);
          setFormData((prev) => ({ ...prev, costCenterId: opt?.value ?? null }));
        },
        required: true,
        disabled: Boolean(createdAreaId),
      },
      { field: 'image', label: 'Area Image', type: 'image', size: 12, directory: 'modelimages', disabled: Boolean(createdAreaId) },
    ],
    [createdAreaId, formatCostCentreOptionLabel, handleCostCentreSearch, selectedAreaCostCentre]
  );

  const { renderField: renderSiteField } = useFormBuilder(step1Fields, site, setSite, errors);
  const { renderField: renderTeamField } = useFormBuilder(step2Fields, team, setTeam, errors);
  const { renderField: renderAssetField } = useFormBuilder(assetFields, asset, setAsset, errors);
  const { renderField: renderAreaField } = useFormBuilder(areaFields, area, setArea, errors);
  const inviteEmailErrors = useMemo(
    () =>
      invites.map((invite) => {
        const emailValue = invite.email?.trim() || '';
        if (!emailValue) {
          return '';
        }

        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)
          ? ''
          : 'Enter a valid email address';
      }),
    [invites],
  );

  const workOrderFields = useMemo(
    () => [
      {
        field: 'taskTypeId',
        label: 'What type of work are you doing?',
        type: 'select',
        size: 12,
        options: [
          { value: 1, label: 'Component Change Out' },
          { value: 2, label: 'Preventative Maintenance' },
          { value: 3, label: 'Inspection' },
          { value: 4, label: 'General Maintenance' },
        ],
        required: true,
      },
      { field: 'workOrderTitle', label: 'Title', type: 'text', size: 12, required: true },
      { field: 'workOrderDescription', label: 'Description', type: 'textarea', size: 12 },
      { field: 'workOrderImage', label: 'Work Order Image', type: 'image', size: 12, required: false, directory: 'workorderfiles' },
      { field: 'scheduledAt', label: 'Scheduled Start', type: 'datetime', size: 6, required: true },
      {
        field: 'dueDate',
        label: 'Scheduled End',
        type: 'datetime',
        size: 6,
        required: true,
        mustBeAfterField: 'scheduledAt',
        mustBeAfterFieldMessage: 'Due date must be after date start',
      },
    ],
    [assetType]
  );
  const { renderField: renderWorkOrderField } = useFormBuilder(workOrderFields, workOrder, setWorkOrder, errors);

  useEffect(() => {
    getPlanningType().then(setPlanningTypes).catch(() => { });
    getAssetType().then(setAssetTypes).catch(() => { });
  }, []);

  useEffect(() => {
    const selected = assetTypes.find((t) => t.id === asset.AssetTypeId);
    if (
      selected &&
      selected.defaultPlanningType &&
      !asset.PlanningTypeId
    ) {
      setAsset((prev) => ({
        ...prev,
        PlanningTypeId: selected.defaultPlanningType,
      }));
    }
  }, [assetTypes, asset.AssetTypeId, asset.PlanningTypeId]);

  useEffect(() => {
    if (!asset.groupId) return;
    if (selectedGroup?.value === asset.groupId) {
      return;
    }
    (async () => {
      try {
        const grp = await getGlobalGroupById(asset.groupId);
        setAsset((prev) => ({
          ...prev,
          assetDescription: `${grp.globalManufacturer?.manufacturer || ''} ${grp.groupName || ''} ${grp.globalClass?.className || ''}`.trim(),
          assetImage: grp.groupImage || prev.assetImage,
        }));
        setSelectedGroup({
          value: grp.id,
          isLocal: false,
          label: grp.groupName || '',
          groupImage: grp.groupImage || '',
          assetDescription: `${grp.globalManufacturer?.manufacturer || ''} ${grp.groupName || ''} ${grp.globalClass?.className || ''}`.trim(),
        });
      } catch (err) {
        try {
          const localGroup = await getLocalGroupById(asset.groupId);
          setAsset((prev) => ({
            ...prev,
            assetDescription: `${localGroup.manufacturer?.manufacturer || ''} ${localGroup.groupName || ''} ${localGroup.class?.className || ''}`.trim(),
            assetImage: localGroup.groupImage || prev.assetImage,
          }));
          setSelectedGroup({
            value: localGroup.id,
            isLocal: true,
            label: localGroup.groupName || '',
            groupImage: localGroup.groupImage || '',
            assetDescription: `${localGroup.manufacturer?.manufacturer || ''} ${localGroup.groupName || ''} ${localGroup.class?.className || ''}`.trim(),
          });
        } catch (localErr) {
          console.error('Error setting selected group', localErr);
        }
      }
    })();
  }, [asset.groupId, selectedGroup?.value]);

  useEffect(() => {
    if (currentStep === 4) {
      const start = new Date();
      start.setMinutes(0, 0, 0);
      start.setHours(start.getHours() + 1);
      const end = new Date(start);
      end.setFullYear(end.getFullYear() + 7);
      setAsset((prev) => ({
        ...prev,
        assetStartDate: prev.assetStartDate || start.toISOString().slice(0, 10),
        assetEndDate: prev.assetEndDate || end.toISOString().slice(0, 10),
        costCenterId: prev.costCenterId || defaultCostCentreId,
        assetDepartmentId: prev.assetDepartmentId || departmentId,
        plantStatusId: prev.plantStatusId || 6,
      }));
      setArea((prev) => ({
        ...prev,
        costCenterId: prev.costCenterId || defaultCostCentreId,
      }));
    }
  }, [currentStep, defaultCostCentreId, departmentId]);

  useEffect(() => {
    hydrateCostCentreSelection(asset.costCenterId, setSelectedAssetCostCentre);
  }, [asset.costCenterId, hydrateCostCentreSelection]);

  useEffect(() => {
    hydrateCostCentreSelection(area.costCenterId, setSelectedAreaCostCentre);
  }, [area.costCenterId, hydrateCostCentreSelection]);

  useEffect(() => {
    const now = new Date();
    now.setMinutes(0, 0, 0);
    now.setHours(now.getHours() + 1);
    const end = new Date(now);
    end.setHours(end.getHours() + 4);
    setWorkOrder((prev) => ({
      ...prev,
      scheduledAt: prev.scheduledAt || now.toISOString().slice(0, 16),
      dueDate: prev.dueDate || end.toISOString().slice(0, 16),
      assetId: assetType === 'asset' ? createdAssetId : null,
      areaId: assetType === 'area' ? createdAreaId : null,
    }));
  }, [createdAssetId, createdAreaId, assetType]);

  useEffect(() => {
    if (currentStep === 2 && colours.length === 0) {
      getColours()
        .then((fetchedColours) => {
          setColours(fetchedColours);
        })
        .catch(() => { });
    }
  }, [currentStep, colours.length]);

  const getLatestIdFromList = async (loader) => {
    try {
      const response = await loader({
        page: 1,
        pageSize: 1,
        sortField: 'id',
        sortOrder: 'DESC',
      });
      const items = Array.isArray(response)
        ? response
        : response?.data || response?.results || [];
      const latestItem = items.find((item) => item && item.id !== undefined && item.id !== null);
      return latestItem?.id ?? null;
    } catch (error) {
      console.error('Error loading latest record', error);
      return null;
    }
  };

  const getListItems = (response) => {
    if (Array.isArray(response)) {
      return response;
    }
    return response?.data || response?.results || [];
  };

  useEffect(() => {
    if (!prereqsLoaded) {
      return;
    }

    const missingTeam = !hasTeamsRecords;
    const missingAsset = !hasAssetRecords;

    if (!missingTeam && !missingAsset) {
      return;
    }

    setCompleted((prev) => {
      const next = { ...prev };
      if (missingTeam) {
        next[2] = false;
      }
      if (missingAsset) {
        if (!hasMultipleAssetRecords) {
          next[4] = false;
        }
        if (!hasMultipleWorkOrderRecords) {
          next[5] = false;
        }
      }
      return next;
    });

    if (!prereqWarningShownRef.current) {
      const missingParts = [];
      if (missingTeam) missingParts.push('work team');
      if (missingAsset) missingParts.push('asset');
      prereqWarningShownRef.current = true;
    }

    if (missingTeam) {
      setCurrentStep((prev) => (prev > 2 ? 2 : prev));
      return;
    }

    if (missingAsset) {
      setCurrentStep((prev) => (prev > 4 ? 4 : prev));
    }
  }, [prereqsLoaded, hasTeamsRecords, hasAssetRecords, hasAreaRecords, hasMultipleAssetRecords, hasMultipleWorkOrderRecords]);

  const handleNext = async () => {
    let stepValid = true;
    const newErrs = {};
    if (currentStep === 1) {
      Object.assign(newErrs, validateForm(step1Fields, site));
      if (!site.name?.trim()) {
        newErrs.name = 'Name is required';
      }
      const allowed = /^[A-Za-z0-9-/@(),\s]*$/;
      const limits = { name: 100, addressL1: 100, addressL2: 100, city: 40, state: 20, postCode: 10, phone: 15 };
      Object.keys(limits).forEach(k => {
        const val = site[k];
        if (val && val.length > limits[k]) newErrs[k] = `${k} too long`;
        if (val && !allowed.test(val)) newErrs[k] = 'Invalid characters';
      });
      stepValid = Object.keys(newErrs).length === 0;
      setErrors(newErrs);
      if (stepValid) {
        try {
          const savedDepartmentId = await createSiteOrDepartment();
          writeSetupState(setupStateKey, {
            departmentId: savedDepartmentId,
            siteName: site.name?.trim(),
            site: {
              ...site,
              name: site.name?.trim(),
            },
          });
          setHasHierarchyStructureRecords(true);
        } catch (err) {
          console.error('Error saving site:', err);
          const message = err?.response?.data?.message || err?.message || 'Error saving site or department';
          showToastError(Array.isArray(message) ? message[0] : message);
        }
      }
    } else if (currentStep === 2) {
      Object.assign(newErrs, validateForm(step2Fields, team));
      if (team.teamName && team.teamName.length > 30) newErrs.teamName = 'Must be less than 30 characters';
      if (team.colourId === '' || team.colourId === null) newErrs.colourId = 'Select a colour';
      stepValid = Object.keys(newErrs).length === 0;
      setErrors(newErrs);
      if (stepValid) {
        try {
          const teamPayload = { teamName: team.teamName, colourId: team.colourId };
          const savedTeam = await createTeams(teamPayload);
          const savedTeamId = savedTeam?.id || null;
          if (savedTeamId) {
            setTeamId(savedTeamId);
          }
          writeSetupState(setupStateKey, {
            departmentId,
            siteName: site.name?.trim() || '',
            site: {
              name: site.name?.trim() || '',
            },
            teamId: savedTeamId,
            teamName: team.teamName?.trim() || '',
            teamColourId: team.colourId ?? '',
            team: {
              id: savedTeamId,
              teamName: team.teamName?.trim() || '',
              colourId: team.colourId ?? '',
            },
          });
          await completeCurrentStep(currentStep);
          return;
        } catch (err) {
          console.error('Error saving team:', err);
          const message = err?.response?.data?.message || err?.message || 'Error saving work team';
          showToastError(Array.isArray(message) ? message[0] : message);
          stepValid = false;
        }
      }
    } else if (currentStep === 3) {
      inviteEmailErrors.forEach((error, idx) => {
        if (error) newErrs[`email${idx}`] = error;
      });
      stepValid = Object.keys(newErrs).length === 0;
      setErrors(newErrs);
      if (stepValid) {
        const validEmails = invites
          .map((inv) => inv.email.trim())
          .filter((emailValue) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue));
        const uniqueEmails = Array.from(new Set(validEmails));

        if (uniqueEmails.length > 0) {
          try {
            const lookedUpEmails = await Promise.all(
              uniqueEmails.map(async (email) => {
                try {
                  const result = await checkInviteEmailAccount(email);
                  return {
                    email,
                    databaseName: result?.databaseName || null,
                  };
                } catch (err) {
                  console.error(`Error checking email account for ${email}`, err);
                  return { email, databaseName: null };
                }
              }),
            );
            const existingEmails = lookedUpEmails
              .filter((entry) => Boolean(entry.databaseName))
              .map((entry) => entry.email);
            if (existingEmails.length > 0) {
              const emailText =
                existingEmails.length === 1
                  ? existingEmails[0]
                  : existingEmails.join(', ');
              showToastWarning(
                `${emailText} already has an account in another company. The invite will still be sent.`,
              );
            }

            await createInvites({
              tenantId: Number(userProfile?.tenantId),
              emails: uniqueEmails,
            });
            writeSetupState(setupStateKey, {
              departmentId,
              siteName: site.name?.trim() || '',
              site: {
                name: site.name?.trim() || '',
              },
              teamId,
              teamName: team.teamName?.trim() || '',
              teamColourId: team.colourId ?? '',
              team: {
                id: teamId,
                teamName: team.teamName?.trim() || '',
                colourId: team.colourId ?? '',
              },
              invites: invites.map((invite) => ({
                email: invite.email?.trim() || '',
              })),
            });
          } catch (err) {
            console.error('Error sending invite', err);
          }
        }
      }
    } else if (currentStep === 4) {
      const fields = assetFields;
      Object.assign(newErrs, validateForm(fields, asset));
      stepValid = Object.keys(newErrs).length === 0;
      setErrors(newErrs);
      if (stepValid) {
        try {
          const selectedGroupId = Number(asset.groupId);
          if (!Number.isInteger(selectedGroupId) || selectedGroupId <= 0) {
            throw new Error('Please select a valid asset group');
          }
          let description = asset.assetDescription;
          try {
            if (selectedGroup?.isLocal) {
              description = selectedGroup.label || description;
            } else {
              const grp = await getGlobalGroupById(selectedGroupId);
              if (grp) {
                description = `${grp.globalManufacturer?.manufacturer || ''} ${grp.groupName || ''} ${grp.globalClass?.className || ''}`.trim();
              }
            }
          } catch (err) {
            console.error('Error fetching group details', err);
          }
          const { groupId, ...assetPayload } = asset;
          const isLocalGroup = Boolean(selectedGroup?.isLocal);
          const created = await createAsset({
            ...assetPayload,
            assetDescription: description,
            plantStatusId: 6,
            organisationId: 1,
            assetDepartmentId: departmentId,
            ...(isLocalGroup
              ? { groupId: selectedGroupId }
              : { globalGroupId: selectedGroupId }),
          });
          clearCache('groups');
          setCreatedAssetId(created.id);
          setLastCreatedTarget({ type: 'asset', id: created.id });
          await completeCurrentStep(currentStep);
          return;
        } catch (err) {
          console.error('Error saving asset', err);
          const message = err?.response?.data?.message || err?.message || 'Error saving asset';
          showToastError(Array.isArray(message) ? message[0] : message);
          stepValid = false;
        }
      }
    } else if (currentStep === 5) {
      Object.assign(newErrs, validateForm(workOrderFields, workOrder));
      if (workOrder.workOrderTitle && workOrder.workOrderTitle.length > 100) {
        newErrs.workOrderTitle = 'Title too long';
      }
      stepValid = Object.keys(newErrs).length === 0;
      setErrors(newErrs);
      if (stepValid) {
        try {
          const [teamList, assetCountSummary] = await Promise.all([
            getTeams(),
            getAssetCountSummary(),
          ]);
          const hasTeams = getListItems(teamList).length > 0;
          const hasAssets = (Number(assetCountSummary?.count) || 0) > 0;

          if (!hasAssets) {
            showToastWarning('Please create at least one asset before adding a work order.');
            stepValid = false;
            return;
          }

          let resolvedAssetId =
            Number(assetType === 'asset' ? createdAssetId : null) ||
            Number(lastCreatedTarget.type === 'asset' ? lastCreatedTarget.id : null) ||
            Number(createdAssetId) ||
            null;
          if (!resolvedAssetId) {
            resolvedAssetId = Number(await getLatestIdFromList(getAssets)) || null;
          }

          const payload = {
            workOrderTitle: workOrder.workOrderTitle,
            workOrderDescription: workOrder.workOrderDescription,
            scheduledAt: new Date(workOrder.scheduledAt).toISOString(),
            dueDate: new Date(workOrder.dueDate).toISOString(),
            departmentId,
            teamsId: teamId || null,
            organisationId: 1,
            assetId: resolvedAssetId,
            areaId: null,
            taskTypeId: workOrder.taskTypeId,
            workorderFiles: workOrder.workOrderImage ? [workOrder.workOrderImage] : [],
            createdBy: userProfile?.id,
            updatedBy: userProfile?.id,
          };
          const created = await createWorkOrder(payload);
          window.dispatchEvent(new Event('workorders:refresh'));
          if (isAdvancedType && createdAssetId) {
            try {
              const evt = await createEvents({
                name: workOrder.workOrderTitle,
                assetId: createdAssetId,
                startDate: payload.scheduledAt,
                endDate: payload.dueDate,
                plannedStartDate: payload.scheduledAt,
                plannedEndDate: payload.dueDate,
                eventStatus: 5,
                eventTypeId: 1,
                createdBy: userProfile?.id,
              });
              await updateWorkOrder(created.id, { eventId: evt.id });
            } catch (err) {
              console.error('Error creating event', err);
            }
          }
        } catch (err) {
          console.error('Error saving work order', err);
          const message = err?.response?.data?.message || err?.message || 'Error saving work order';
          showToastError(Array.isArray(message) ? message[0] : message);
          stepValid = false;
        }
      }
    }
    if (stepValid) {
      await completeCurrentStep(currentStep);
    }
  };

  const handleSkip = async () => {
    if (currentStep === 1) {
      return;
    }

    if (currentStep === 2 || currentStep === 3) {
      await completeCurrentStep(currentStep);
      return;
    }

    let next = currentStep + 1;
    while (next <= steps.length && completed[next]) next++;
    setCurrentStep(next);
  };

  const handleSkipAll = async () => {
    navigate('/home');
  };

  const completedCount = Object.values(completed).filter(Boolean).length;
  const progress = (completedCount / steps.length) * 100;

  useEffect(() => {
    if (currentStep <= steps.length && !prereqsLoaded && !hasAssetRecords && !hasAreaRecords) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      navigate('/home');
    }, 300000);

    return () => window.clearTimeout(timer);
  }, [currentStep, navigate]);

  const toggleAddressAccordion = () => {
    setAddressOpen(prev => !prev);
  };

  const handleProductTourFromProfile = () => {
    const triggerValue = String(Date.now());
    sessionStorage.setItem(PRODUCT_TOUR_TRIGGER_ONCE_KEY, triggerValue);
    localStorage.removeItem(PRODUCT_TOUR_TRIGGER_KEY);
    window.dispatchEvent(new CustomEvent('product-tour-triggered', { detail: { triggerValue } }));
    navigate('/home');
  };

  const buildDepartmentCode = (name) => {
    const fallback = 'DEPARTMENT';
    const source = (name || fallback)
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    return (source || fallback).slice(0, 25);
  };

  const buildFunctionalLocation = (name) => {
    const fallback = 'New Department';
    return (name || fallback).trim().slice(0, 100);
  };

  const createSiteOrDepartment = async () => {
    const trimmedName = site.name?.trim();
    if (!trimmedName) {
      throw new Error('Site or Department name is required');
    }

    const created = await createHierarchyStructure({
      name: trimmedName,
      code: buildDepartmentCode(trimmedName),
      functionalLocation: buildFunctionalLocation(trimmedName),
      parentId: null,
      sortOrder: 1,
      shiftLogFlag: 0,
      allowEventOverlap: 0,
      isDepartment: 1,
      billingAddressL1: site.addressL1 || null,
      billingAddressL2: site.addressL2 || null,
      billingCity: site.city || null,
      billingState: site.state || null,
      billingPostcode: site.postCode || null,
      billingPhone: site.phone || null,
      deliveryAddressL1: site.addressL1,
      deliveryAddressL2: site.addressL2,
      deliveryCity: site.city,
      deliveryState: site.state,
      deliveryPostCode: site.postCode,
      deliveryPhone: site.phone,
      organisationId,
      createdBy: userProfile?.id,
      updatedBy: userProfile?.id,
    });

    setDepartmentId(created.id);
    writeSetupState(setupStateKey, {
      departmentId: created.id,
      siteName: created.name || trimmedName,
      site: {
        ...site,
        name: created.name || trimmedName,
      },
      department: created,
    });
    return created.id;
  };

  const completeCurrentStep = async (stepNumber) => {
    try {
      await completeSetupTask(steps[stepNumber - 1].key);
    } catch (err) {
      console.error('Error recording task completion', err);
    }

    const newCompleted = { ...completed, [stepNumber]: true };
    setCompleted(newCompleted);
    window.dispatchEvent(new Event('setup-tasks-updated'));
    let next = stepNumber + 1;
    while (next <= steps.length && newCompleted[next]) next++;
    setCurrentStep(next);
  };

  const handleStepJump = (stepNumber) => {
    if (stepNumber === currentStep) {
      return;
    }

    if (stepNumber === 1 && completed[1] && hasHierarchyStructureRecords) {
      return;
    }

    if (stepNumber > currentStep && !completed[stepNumber]) {
      return;
    }

    setCurrentStep(stepNumber);
  };

  const renderStep1 = () => {
    const nameField = step1Fields.find(f => f.field === 'name');
    const addressFields = step1Fields.filter(f => f.field !== 'name');
    return (
      <>
        <h6>Add a Site or Department</h6>
        <p>
          A site or department helps organise your company’s users and assets.
          Sites are the highest level and can include one or more departments.
          They control who has access to what, how reports are grouped, and how
          data is organised. You can edit it later. You can also add an address
          to a site to use as the default delivery location for purchase orders.
        </p>
        <div className="row g-3 mb-3">
          {nameField && renderSiteField(nameField)}
        </div>
        <div className="accordion mb-3" id="addressAccordion">
          <div className="accordion-item">
            <h2 className="accordion-header">
              <button
                className={`accordion-button ${addressOpen ? '' : 'collapsed'}`}
                type="button"
                onClick={toggleAddressAccordion}
                aria-expanded={addressOpen}
                aria-controls="collapseAddress"
              >
                Enter Address
              </button>
            </h2>
            <div
              id="collapseAddress"
              className={`accordion-collapse collapse ${addressOpen ? 'show' : ''}`}
            >
              <div className="accordion-body">
                <div className="row g-3">
                  {addressFields.map(renderSiteField)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const renderStep2 = () => (
    <>
      <h6>Add a Work Team</h6>
      <p className="text-muted">
        A work team is a group of workers that can be assigned to assets and areas. Workers can be assigned to more than one work team. Typically a Work Team is responsible for a site, department or group of assets.
      </p>
      <div className="row g-3 mb-3">
        {step2Fields.map(renderTeamField)}
      </div>
    </>
  );

  const renderStep3 = () => (
    <>
      <h6>Invite Team Members</h6>
      <p className="text-muted">Invite team members to join you in Samurai.</p>
      {invites.map((m, idx) => (
        <div className="row g-3 mb-4" key={idx}>
          <div className="col">
            <input
              placeholder="Email"
              className={`form-control ${inviteEmailErrors[idx] ? 'is-invalid' : ''}`}
              value={m.email}
              onChange={e => {
                const arr = [...invites];
                arr[idx].email = e.target.value;
                setInvites(arr);
              }}
            />
            {inviteEmailErrors[idx] && (
              <div className="invalid-feedback d-block">{inviteEmailErrors[idx]}</div>
            )}
          </div>
          <div className="col-auto d-flex align-items-center">
            {invites.length > 1 && (
              <button
                type="button"
                className="btn btn-link text-danger p-0 text-decoration-none"
                onClick={() => {
                  setInvites((current) => current.filter((_, currentIdx) => currentIdx !== idx));
                }}
                aria-label={`Remove invite ${idx + 1}`}
              >
                <Icon icon="mdi:trash-can-outline" className="text-lg" />
              </button>
            )}
          </div>
        </div>
      ))}
      <div className="d-flex justify-content-end">
        <button
          className="btn btn-primary btn-sm mt-3"
          onClick={() => setInvites([...invites, { email: '' }])}
        >
          + Add another
        </button>
      </div>

    </>
  );

  const renderStep4 = () => (
    <>
      <h6>Add an Asset</h6>
      <p className="text-muted mb-32">Assets represent the equipment your team
        maintains or works on. Every work order needs to be linked to an asset
        so the system knows where the work is happening.</p>
      <div className="row g-3">{assetFields.map(renderAssetField)}</div>
    </>
  );

  const renderStep5 = () => (
    <>
      <h6>Add a Work Order</h6>
      <p className="text-muted">An actionable job instruction generated from a task or work request. Work orders include job scope, parts, labour, documents, and technician assignments. They form the core of maintenance execution in Samurai.</p>
      <div className="row g-3">{workOrderFields.map(renderWorkOrderField)}</div>
    </>
  );


  const renderCurrent = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      default: return null;
    }
  };

  const handleCloseAddGroupModal = () => {
    setShowAddGroupModal(false);
    setNewGroup({});
    setGroupErrors({});
  };

  const handleSendCompanionAppLink = async (platform) => {
    const trimmedPhone = appPhoneNumber.trim();
    const targetLink = platform === 'ios' ? iosCompanionAppLink : androidCompanionAppLink;
    const platformLabel = platform === 'ios' ? 'iOS' : 'Android';
    const smsBody = `Download the Samurai Companion App (${platformLabel}): ${targetLink}`;

    try {
      if (trimmedPhone) {
        const smsHref = `sms:${encodeURIComponent(trimmedPhone)}?body=${encodeURIComponent(smsBody)}`;
        window.location.href = smsHref;
      } else if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(smsBody);
      }

      setAppLinkMessage(
        trimmedPhone
          ? `Opened your message app with the ${platformLabel} download link.`
          : `${platformLabel} download link copied. Add a phone number to send via SMS.`,
      );
    } catch (_error) {
      setAppLinkMessage(`Unable to send ${platformLabel} link automatically. Use the direct store link below.`);
    }
  };

  const handleGoToHomePage = () => {
    sessionStorage.setItem(HOME_SKIP_AUTO_TOUR_ONCE_KEY, 'true');
    navigate('/home');
  };

  if (currentStep > steps.length && prereqsLoaded) {
    return (
      <div className="card p-24 overflow-hidden">
        <div className="row align-items-center g-4">
          <div className="col-lg-6">
            <span className="badge bg-success-100 text-success px-12 py-8 rounded-pill mb-16">
              Onboarding complete
            </span>
            <h3 className="mb-12">Great job, you are all set.</h3>
            <p className="text-secondary-light mb-20">
              Your site, team, assets, and first work order are ready. You can now head to Home and start using the app.
            </p>
            <div className="d-flex flex-wrap gap-12">
              <button
                type="button"
                className="btn btn-primary px-20 py-12 fw-semibold d-inline-flex align-items-center gap-2"
                onClick={handleGoToHomePage}
              >
                <Icon icon="mdi:home-outline" />
                <span>Go To Home page</span>
              </button>
              <button
                type='button'
                className="btn btn-outline-primary px-20 py-12 fw-semibold d-inline-flex align-items-center gap-2"
                onClick={handleProductTourFromProfile}
              >
                <Icon icon='mdi:compass-outline' className='icon text-xl' />
                Take a Product Tour
              </button>
              <Link to="/data-loading" className="btn btn-outline-primary px-20 py-12 fw-semibold d-inline-flex align-items-center gap-2">
                <Icon icon="mdi:database-import-outline" />
                <span>Bulk Load Data</span>
              </Link>
              <a
                href="https://help.samurai-cmms.com.au/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-outline-primary px-20 py-12 fw-semibold d-inline-flex align-items-center gap-2"
              >
                <Icon icon="mdi:book-open-page-variant-outline" />
                <span>Help Docs</span>
              </a>
            </div>
            <div className="mt-20 border rounded-3 p-16">
              <h6 className="mb-8 d-flex align-items-center gap-2">
                <Icon icon="mdi:cellphone-link" />
                <span>Companion App</span>
              </h6>
              <p className="text-secondary-light mb-12">
                Send the mobile app link to your phone and continue onboarding on iOS or Android.
              </p>
              <div className="row g-2">
                <div className="col-12 col-md-6">
                  <label className="form-label mb-6">Phone Number (optional)</label>
                  <input
                    type="tel"
                    className="form-control"
                    placeholder="+61 4xx xxx xxx"
                    value={appPhoneNumber}
                    onChange={(event) => setAppPhoneNumber(event.target.value)}
                  />
                </div>
                <div className="col-12 col-md-6 d-flex flex-wrap align-items-end gap-8">
                  <button
                    type="button"
                    className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
                    onClick={() => handleSendCompanionAppLink('android')}
                  >
                    <Icon icon="mdi:android" />
                    <span>Send Android Link</span>
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-primary d-inline-flex align-items-center gap-2"
                    onClick={() => handleSendCompanionAppLink('ios')}
                  >
                    <Icon icon="mdi:apple-ios" />
                    <span>Send iOS Link</span>
                  </button>
                </div>
                <div className="col-12">
                  <div className="d-flex flex-wrap gap-40 align-items-start">
                    <div className="text-center p-16 border rounded-3 bg-white">
                      <div className="small text-muted mb-6">Android QR</div>
                      {androidQrCodeUrl ? (
                        <img src={androidQrCodeUrl} alt="Android app QR code" style={{ width: '110px', height: '110px' }} />
                      ) : (
                        <div className="border rounded d-flex align-items-center justify-content-center" style={{ width: '110px', height: '110px' }}>
                          <span className="small text-muted">Loading...</span>
                        </div>
                      )}
                    </div>
                    <div className="text-center p-16 border rounded-3 bg-white">
                      <div className="small text-muted mb-6">iOS QR</div>
                      {iosQrCodeUrl ? (
                        <img src={iosQrCodeUrl} alt="iOS app QR code" style={{ width: '110px', height: '110px' }} />
                      ) : (
                        <div className="border rounded d-flex align-items-center justify-content-center" style={{ width: '110px', height: '110px' }}>
                          <span className="small text-muted">Loading...</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="d-flex flex-wrap gap-40 align-items-start">
                    <a href={androidCompanionAppLink} target="_blank" rel="noopener noreferrer" className="d-inline-block">
                      <img
                        src={googlePlayBadgeImage}
                        alt="Get it on Google Play"
                        style={{ height: '44px', width: 'auto' }}
                      />
                    </a>
                    <a href={iosCompanionAppLink} target="_blank" rel="noopener noreferrer" className="d-inline-block">
                      <img
                        src={appStoreBadgeImage}
                        alt="Download on the App Store"
                        style={{ height: '44px', width: 'auto' }}
                      />
                    </a>
                  </div>
                </div>
              </div>
              {appLinkMessage && (
                <div className="text-muted small mt-10">{appLinkMessage}</div>
              )}
            </div>
            <div className="text-secondary-light mt-16 mb-0">
              <p className="mb-8">You can also review the following pages:</p>
              <ul className="mb-0">
                <li><Link to="/dashboards/overview" className="fw-semibold text-primary">
                  Dashboard
                </Link></li>
                <li><Link to="/work-orders" className="fw-semibold text-primary">
                  View Work Orders
                </Link></li>
                <li><Link to="/asset-list" className="fw-semibold text-primary">
                  View the Assets List
                </Link></li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-24 position-relative">
      <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
        <p className="mb-0">Complete the following setup to get started with better maintenance management.</p>
      </div>

      <div className="row">
        <div className="col-md-4 mb-3 rounded-2 p-3">
          <p className="d-flex justify-content-between align-items-center fw-bold text-xl">
            Setup your new system
            <span className="text-sm fw-normal">
              {currentStep} of {steps.length}
            </span>
          </p>

          <div className="mb-3">
            <div className="progress progress-sm">
              <div className="progress-bar bg-primary" style={{ width: `${progress}%` }} />
            </div>
          </div>
          <ul className="list-group">
            {steps.map((step, i) => {
              const stepNumber = i + 1;
              const isCompleted = completed[stepNumber];
              const isLocked = stepNumber === 1 && isCompleted && hasHierarchyStructureRecords;
              return (
                <li
                  key={i}
                  className={`p-0 mb-8 border-0 ${isLocked ? 'opacity-50 pe-none' : ''}`}
                  aria-disabled={isLocked}
                  style={isLocked ? { pointerEvents: 'none' } : undefined}
                >
                  <button
                    type="button"
                    onClick={() => handleStepJump(stepNumber)}
                    className={`w-100 text-start position-relative p-12 rounded-2 border ${currentStep === stepNumber ? 'fw-semibold border-neutral-400' : ''
                      } ${isCompleted ? 'bg-success-100' : ''} ${isLocked ? 'cursor-not-allowed' : ''}`}
                    style={{ height: '80px', borderWidth: '0.25px' }}
                    disabled={(stepNumber > currentStep && !completed[stepNumber]) || isLocked}
                    data-tg-tour={`Open ${step.title.toLowerCase()} to complete this setup milestone and keep onboarding moving.`}
                    data-tg-group="setup-tour"
                    data-tg-order={2 + i}
                    data-tg-title={step.title}
                  >
                    <Icon
                      icon={isCompleted ? 'mdi:check-circle' : step.icon}
                      className={`menu-icon text-2xl me-3 ${isCompleted ? 'text-success' : ''}`}
                      style={{ position: 'relative', top: '-4px', fontSize: isCompleted ? '1.5rem' : undefined }}
                    />
                    {step.title}
                    <Icon
                      icon={currentStep === stepNumber ? 'mage:caret-right' : ''}
                      className="position-absolute"
                      style={{ top: '8px', right: '12px', fontSize: '20px' }}
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="col-md-8 mb-16">
          <div className='border p-16 rounded-2 ms-4 h-100 '>
            {renderCurrent()}
          </div>
        </div>
      </div>
      <div className="d-flex justify-content-end mt-4 gap-2">
        {currentStep <= steps.length && (
          <button
            className="btn btn-outline-secondary"
            onClick={handleSkipAll}
            data-tg-tour="Skip the rest if you are not ready yet and want to return later."
            data-tg-group="setup-tour"
            data-tg-order="8"
            data-tg-title="Skip all"
          >
            Skip All
          </button>
        )}
        {currentStep <= steps.length && currentStep !== 1 && (
          <button
            className="btn btn-outline-primary"
            onClick={handleSkip}
            data-tg-tour="Skip only this step when the detail is not needed right now."
            data-tg-group="setup-tour"
            data-tg-order="9"
            data-tg-title="Skip step"
          >
            Skip this step
          </button>
        )}
        <button
          className="btn btn-primary"
          onClick={handleNext}
          disabled={currentStep === 3 && inviteEmailErrors.some(Boolean)}
          data-tg-tour="Save what you have entered and move on to the next part of setup."
          data-tg-group="setup-tour"
          data-tg-order="10"
          data-tg-title="Save and continue"
        >
          {currentStep === steps.length
            ? 'Save and finish'
            : 'Save and continue'}
        </button>
      </div>
      {showAddCostCentreModal && (
        <div className="modal show d-block" tabIndex="-1" role="dialog" aria-modal="true">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">New Cost Centre</h5>
                <button type="button" className="btn-close" onClick={handleCloseAddCostCentreModal} />
              </div>
              <div className="modal-body">
                <div className="row">{costCentreInputFields.map(renderCostCentreField)}</div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseAddCostCentreModal}>
                  Close
                </button>
                <button type="button" className="btn btn-primary" onClick={handleCostCentreCreateSave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showAddGroupModal && (
        <div className="modal show d-block" tabIndex="-1" role="dialog" aria-modal="true">
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">New Model or Group</h5>
                <button type="button" className="btn-close" onClick={handleCloseAddGroupModal} />
              </div>
              <div className="modal-body">
                <div className="row g-3">{groupInputFields.map(renderGroupField)}</div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseAddGroupModal}>
                  Close
                </button>
                <button type="button" className="btn btn-primary" onClick={handleGroupCreateSave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SetupLayer;
